import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '../common/localStorage';
import type { AuthReadyEventDetail } from '../global';
import type { LoginReason } from '@tv2no/tv2no-snowplow-tracking';

/**
 * To prevent spamming user api, we store the user and token in localstorage
 */
export const handleAppLogin = () => {
  const { userApiUrl } = window.AUTH0_CONFIG;

  const login = async function (reasonOrCategory?: string | LoginReason, reasonName?: string) {
    let data: any;

    if (typeof reasonOrCategory === 'object' && reasonOrCategory !== null && 'category' in reasonOrCategory) {
      const reason = reasonOrCategory as LoginReason;
      data = {
        reasonToLoginCategory: reason.category,
        reasonToLoginName: reason.action,
        loginReason: reason,
      };
    } else {
      data = {
        reasonToLoginCategory: reasonOrCategory,
        reasonToLoginName: reasonName,
      };
    }

    if (window?.webkit?.messageHandlers?.onLoginEvent) {
      window.webkit.messageHandlers.onLoginEvent.postMessage(JSON.stringify(data));
    }

    if (typeof window.TV2JSBridge !== 'undefined' && typeof window?.TV2JSBridge?.onLoginEvent === 'function') {
      window.TV2JSBridge.onLoginEvent(JSON.stringify(data));
    }
  };

  const getLoggedInUser = async function () {
    try {
      const token = await getToken();

      if (!token) {
        removeLocalStorageItem('appAuthToken');
        removeLocalStorageItem('appUser');
        return;
      }

      const appUser = getLocalStorageItem('appUser');

      if (appUser) {
        return appUser;
      }

      const userResponse = await fetch(userApiUrl, {
        method: 'get',
        credentials: 'include',
        headers: new Headers({
          Authorization: 'Bearer ' + token,
        }),
      });

      const user = await userResponse.json();

      setLocalStorageItem('appUser', user, 6);

      return user;
    } catch (e) {
      return;
    }
  };

  const getToken = async function () {
    // IOS
    if (window?.webkit?.messageHandlers?.getToken) {
      return await window.webkit.messageHandlers.getToken.postMessage('getToken');
    }

    // Android
    if (typeof window.TV2JSBridge !== 'undefined' && typeof window.TV2JSBridge?.getToken === 'function') {
      return await window.TV2JSBridge.getToken();
    }

    // TODO: Remove when all apps are exposing native getToken()
    return getLocalStorageItem('appAuthToken') ?? '';
  };

  const isAuthenticated = async function () {
    const token = await getToken();

    return !!(token && token !== '' && token?.length > 100);
  };

  window.TV2 = {
    ...window.TV2,
    login,
    getLoggedInUser,
    getToken,
    isAuthenticated,
  };

  const init = async function () {
    const user = await getLoggedInUser();
    const userId = user?.id;

    // Deprecated - still in use until all uses are updated to new event
    document.dispatchEvent(
      new CustomEvent('tv2-menu:auth0-ready', {
        detail: {
          app: true,
          isReady: true,
          userId,
        } as AuthReadyEventDetail,
      }),
    );

    // New event to take over for above event
    document.dispatchEvent(
      new CustomEvent('tv2-menu:auth-ready', {
        detail: {
          app: true,
          isReady: true,
          userId,
        } as AuthReadyEventDetail,
      }),
    );
  };

  init();
};
